/**
 * MentionsLane - the Mentions lane on Today (docs/plans/crm-report-builder-search-collab-plan.md A3.3,
 * A3.4 and B3.4): my unread @mentions and the replies to notes I wrote, newest first, just after
 * "AI reviews ready" and before Callbacks.
 *
 * Scoped to the person MENTIONED, never to the lead's owner or coverage (like "AI reviews ready"),
 * and read from Today's own worklist answer (`buckets.mentions`, at most 50 rows): no fetch and no
 * poll of its own. Hidden at zero like every lane on Today (Today.tsx Section), and on an api that
 * predates mentions (the bucket is absent).
 *
 * A row opens the lead on that note (/crm/contacts/<id>?note=<note id>) like a real link, and
 * opening the lead is what marks the mention read (decision M4 a; ContactDetail). "Mark all read"
 * clears the lane; "See all" opens the Mentions page, read and unread.
 *
 * MentionRow is shared with the Mentions page, so a mention reads the same in both places; every
 * word comes from mentionLogic's laneWords.
 */
import { useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AtSign, ChevronRight, Loader2 } from 'lucide-react';
import { notifyStatsChanged } from '../lib';
import { RowLink, useRowNav } from '../components/shared';
import type { WorklistMentionsBucket } from '../types';
import { markMentionsRead, mentionErrorMessage } from './api';
import { laneWords, parseMentionsBucket, type MentionNow } from './mentionLogic';
import type { MentionItem } from './types';

/** Where "See all" goes: the Mentions page (routed by CRMApp). */
export const MENTIONS_PAGE_HREF = '/crm/mentions';

/** The Today row recipe (Today.tsx worklistRowCls), so the lane reads like its neighbours. */
const ROW_CLS =
  'flex cursor-pointer items-center gap-3 py-3 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20';

const KIND_CHIP =
  'inline-flex flex-shrink-0 items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300';

/** A quiet text action in a lane header: a 44 px target on a phone, compact from sm up. */
export const laneActionCls =
  'inline-flex min-h-[44px] items-center gap-1 rounded text-xs font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:text-primary-400 dark:hover:text-primary-300 sm:min-h-0';

export interface MentionRowProps {
  item: MentionItem;
  /** A user id to a display name (useTeam().byId), so the headline names the author. */
  nameOf?: (userId: string) => string | null | undefined;
  /** Mark unread rows with a dot and bold type (the Mentions page, where read rows show too). */
  markUnread?: boolean;
  /** "Now" for the time words; the current time when absent. */
  now?: MentionNow;
}

/**
 * One mention as a row that opens like a real link (useRowNav + RowLink): the lead, scrolled to
 * that note. "Jane Smith [Mention] Luke mentioned you" over the note's first line, then when.
 */
export function MentionRow({ item, nameOf, markUnread = false, now }: MentionRowProps) {
  const rowNav = useRowNav();
  const navigate = useNavigate();
  const w = laneWords(item, now, { nameOf });
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && e.target === e.currentTarget) navigate(w.href);
  };
  const strong = !markUnread || w.unread;
  return (
    <div
      role="button"
      tabIndex={0}
      {...rowNav(w.href)}
      onKeyDown={onKeyDown}
      className={ROW_CLS}
      data-mention-id={item.id}
    >
      {markUnread && (
        <span
          aria-hidden="true"
          className={`h-2 w-2 flex-shrink-0 rounded-full ${w.unread ? 'bg-primary-600 dark:bg-primary-400' : 'bg-transparent'}`}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <RowLink
            to={w.href}
            title={w.lead}
            className={`min-w-0 truncate text-sm text-gray-900 dark:text-gray-100 ${strong ? 'font-semibold' : 'font-medium'}`}
          >
            {w.lead}
          </RowLink>
          <span className={KIND_CHIP}>{w.kindLabel}</span>
          <span className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">{w.headline}</span>
          {markUnread && w.unread && <span className="sr-only">(unread)</span>}
        </div>
        {w.line && (
          <p
            className={`mt-0.5 truncate text-xs ${w.deleted ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}
          >
            {w.line}
          </p>
        )}
      </div>
      {w.when && <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">{w.when}</span>}
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" aria-hidden="true" />
    </div>
  );
}

export interface MentionsLaneProps {
  /** Today's `buckets.mentions` (the full worklist read, with its rows); absent on an older api. */
  bucket: WorklistMentionsBucket | null | undefined;
  /** A user id to a display name (useTeam().byId). */
  nameOf?: (userId: string) => string | null | undefined;
  /** "Now" for the time words; the current time when absent. */
  now?: MentionNow;
}

/** The Mentions lane: nothing at all while there is nothing unread. */
export default function MentionsLane({ bucket, nameOf, now }: MentionsLaneProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The answer that "Mark all read" emptied: the lane stays hidden until Today's next read (which
  // notifyStatsChanged starts at once) brings a new one.
  const [clearedFor, setClearedFor] = useState<WorklistMentionsBucket | null>(null);
  // Read defensively: a malformed body is an absent bucket, and a row that cannot open is dropped.
  const view = useMemo(() => parseMentionsBucket(bucket), [bucket]);

  if (!bucket || !view || view.count <= 0 || clearedFor === bucket) return null;
  const items = view.items || [];
  const more = Math.max(0, view.count - items.length);

  const markAll = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await markMentionsRead({ all: true });
      setClearedFor(bucket);
      // The badge, this lane and any mention card clear on the one count poll.
      notifyStatsChanged();
    } catch (e) {
      setError(mentionErrorMessage(e, "Couldn't mark your mentions read. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-labelledby="crm-mentions-lane-title"
      className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <AtSign className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <h2 id="crm-mentions-lane-title" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Mentions
        </h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">({view.count.toLocaleString()})</span>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden text-xs text-gray-400 sm:block">Notes that name you, and replies to yours</span>
          <button type="button" onClick={markAll} disabled={busy} className={laneActionCls}>
            {busy && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
            Mark all read
          </button>
          <Link to={MENTIONS_PAGE_HREF} className={laneActionCls}>
            See all
          </Link>
        </div>
      </div>
      {error && (
        <p role="alert" className="border-b border-gray-100 px-4 py-2 text-xs text-red-600 dark:border-gray-700 dark:text-red-400">
          {error}
        </p>
      )}
      <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">
        {items.map((item) => (
          <MentionRow key={item.id} item={item} nameOf={nameOf} now={now} />
        ))}
        {more > 0 && (
          <p className="py-2.5 text-xs text-gray-500 dark:text-gray-400">
            {more.toLocaleString()} more unread.{' '}
            <Link to={MENTIONS_PAGE_HREF} className="font-medium text-primary-700 hover:underline dark:text-primary-400">
              See them all
            </Link>
          </p>
        )}
      </div>
    </section>
  );
}
